/**
 * The mock interview.
 *
 * The Question Bank next door is a drill: you pick the question and reveal the answer whenever
 * you like. This is rehearsal, and the difference is one rule —
 *
 *   YOU DO NOT CHOOSE THE QUESTION, AND YOU SEE NO ANSWER UNTIL THE END.
 *
 * That is enforced server-side: the API hands over one question at a time and keeps the running
 * order in the session row, so there is no list here to leak and nothing to read ahead in. This
 * component only has to avoid undoing it — which mostly means resisting the urge to add a
 * "skip" or "show answer" button, because both would turn it back into a drill.
 */
import { useState } from 'react';
import { ArrowRight, ChartBar, CheckCircle, PaperPlaneTilt, Target, User, Warning } from '@phosphor-icons/react';
import type { BankDebrief, BankSessionState } from '@cvs/shared';
import { BANK_POSITIONS, BANK_SESSION_LENGTHS, BANK_TOPIC_LABELS, BANK_TOPICS } from '@cvs/shared';
import { api } from '../lib/api';
import { Button, Card, EmptyState, Pill, cx } from './ui';

type Phase = 'setup' | 'running' | 'debrief';

const KIND_LABEL: Record<string, string> = {
  technical: 'Technical',
  behavioural: 'Behavioural',
  scenario: 'Scenario',
};

export function MockInterviewPanel() {
  const [phase, setPhase] = useState<Phase>('setup');
  const [scope, setScope] = useState<'topic' | 'position'>('position');
  const [scopeValue, setScopeValue] = useState(BANK_POSITIONS[0].id);
  const [length, setLength] = useState<number>(8);

  const [state, setState] = useState<BankSessionState | null>(null);
  const [debrief, setDebrief] = useState<BankDebrief | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const s = await api.startBankSession({ scope, scopeValue, length });
      setState(s);
      setPhase('running');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the interview.');
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    const answer = draft.trim();
    if (!answer || !state || busy) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api.answerBankSession(state.sessionId, { answer, isFollowUp: !!state.followUp });
      setDraft('');
      setState(next);
      if (next.status === 'complete') {
        const done = await api.bankSession(state.sessionId);
        if ('debrief' in done && done.debrief) setDebrief(done.debrief);
        setPhase('debrief');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit that answer.');
    } finally {
      setBusy(false);
    }
  }

  if (phase === 'setup') {
    return (
      <div className="space-y-5">
        <Card className="space-y-5 p-6">
          <div className="space-y-1">
            <h3 className="font-display text-lg font-semibold text-navy">Set up your interview</h3>
            <p className="text-sm text-slate-500">
              You will get one question at a time, sometimes a follow-up, and no answers until the end. That is the
              point — rehearsing with the answer a click away is not rehearsing.
            </p>
          </div>

          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-teal-700">What should it cover?</p>
            <div className="flex gap-2">
              <Button variant={scope === 'position' ? 'primary' : 'ghost'} onClick={() => { setScope('position'); setScopeValue(BANK_POSITIONS[0].id); }}>
                <Target size={16} weight="bold" /> A role
              </Button>
              <Button variant={scope === 'topic' ? 'primary' : 'ghost'} onClick={() => { setScope('topic'); setScopeValue(BANK_TOPICS[0]); }}>
                <ChartBar size={16} weight="bold" /> One topic
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              {scope === 'position'
                ? 'A role draws across topics the way a real panel does.'
                : 'A single topic is for when you already know where you are weak.'}
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-3">
            {(scope === 'position' ? BANK_POSITIONS.map((p) => ({ id: p.id, label: p.label })) : BANK_TOPICS.map((t) => ({ id: t, label: BANK_TOPIC_LABELS[t] ?? t }))).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setScopeValue(opt.id)}
                className={cx(
                  'rounded-xl border p-3 text-left text-sm transition',
                  scopeValue === opt.id ? 'border-teal-500 bg-teal-50 text-navy' : 'border-slate-200 text-slate-600 hover:border-slate-300',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-teal-700">How many questions?</p>
            <div className="flex gap-2">
              {BANK_SESSION_LENGTHS.map((n) => (
                <Button key={n} variant={length === n ? 'primary' : 'ghost'} onClick={() => setLength(n)}>
                  {n}
                </Button>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
          <Button onClick={start} disabled={busy}>
            {busy ? 'Starting…' : 'Start the interview'} <ArrowRight size={16} weight="bold" />
          </Button>
        </Card>
      </div>
    );
  }

  if (phase === 'running' && state?.question) {
    const q = state.question;
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
            Question {state.position} of {state.total}
          </p>
          <div className="flex gap-1.5">
            {Array.from({ length: state.total }, (_, i) => (
              <span key={i} className={cx('h-1.5 w-6 rounded-full', i < state.position - 1 ? 'bg-teal-500' : i === state.position - 1 ? 'bg-navy' : 'bg-slate-200')} />
            ))}
          </div>
        </div>

        <Card className="space-y-4 p-6">
          <div className="flex flex-wrap gap-2">
            <Pill>{BANK_TOPIC_LABELS[q.topic] ?? q.topic}</Pill>
            <Pill>{KIND_LABEL[q.kind] ?? q.kind}</Pill>
          </div>

          <p className="font-display text-lg font-semibold leading-snug text-navy">{q.question}</p>

          {state.followUp && (
            <div className="rounded-xl border-l-2 border-teal-500 bg-teal-50/60 p-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-teal-700">Follow-up</p>
              <p className="mt-1 text-sm text-navy">{state.followUp}</p>
            </div>
          )}

          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={7}
            placeholder={state.followUp ? 'Answer the follow-up…' : 'Say it the way you would say it out loud.'}
            className="w-full rounded-xl border border-slate-200 p-3 text-sm leading-relaxed outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
          />

          {error && <p className="text-sm text-rose-600">{error}</p>}

          <div className="flex items-center justify-between">
            {/* No skip, and no reveal. Both exist next door in the Question Bank, where they belong. */}
            <p className="text-xs text-slate-400">You will see every answer in the debrief.</p>
            <Button onClick={submit} disabled={busy || !draft.trim()}>
              {busy ? 'Sending…' : state.position === state.total && !state.followUp ? 'Finish' : 'Next'}
              <PaperPlaneTilt size={16} weight="bold" />
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (phase === 'debrief' && debrief) {
    return <Debrief debrief={debrief} onRestart={() => { setPhase('setup'); setState(null); setDebrief(null); }} />;
  }

  return <EmptyState title="Interview finished" description="Nothing more to answer." />;
}

function Debrief({ debrief, onRestart }: { debrief: BankDebrief; onRestart: () => void }) {
  return (
    <div className="space-y-5">
      <Card className="space-y-4 p-6">
        <h3 className="font-display text-lg font-semibold text-navy">How that went</h3>
        <div className="flex flex-wrap gap-6">
          <Stat label="Questions" value={String(debrief.items.length)} />
          {/* Shown only when something was actually graded — a dash beats a fabricated zero. */}
          <Stat label="Average" value={debrief.overall === null ? '—' : `${Math.round(debrief.overall)}%`} />
          <Stat label="Behavioural" value={String(debrief.behaviouralCount)} />
        </div>
        {debrief.strongestTopic && debrief.weakestTopic && (
          <p className="text-sm text-slate-600">
            Strongest on <strong className="text-navy">{BANK_TOPIC_LABELS[debrief.strongestTopic] ?? debrief.strongestTopic}</strong>,
            thinnest on <strong className="text-navy">{BANK_TOPIC_LABELS[debrief.weakestTopic] ?? debrief.weakestTopic}</strong>.
          </p>
        )}
        <Button onClick={onRestart}>Run another</Button>
      </Card>

      {debrief.items.map((item, i) => (
        <Card key={item.questionId} className="space-y-4 p-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[11px] text-slate-400">{i + 1}</span>
            <Pill>{BANK_TOPIC_LABELS[item.topic] ?? item.topic}</Pill>
            <Pill>{KIND_LABEL[item.kind] ?? item.kind}</Pill>
            {item.score !== null && <Pill>{Math.round(item.score)}%</Pill>}
            {item.kind === 'behavioural' && <Pill>Not scored</Pill>}
          </div>

          <p className="font-display font-semibold leading-snug text-navy">{item.question}</p>

          <Block icon={<User size={14} weight="bold" />} label="Your answer" tone="plain">
            {item.yourAnswer || <em className="text-slate-400">No answer given.</em>}
          </Block>

          {item.followUp && (
            <Block icon={<ArrowRight size={14} weight="bold" />} label="Follow-up asked" tone="teal">
              <p className="mb-2 italic">{item.followUp}</p>
              {item.followUpAnswer && <p>{item.followUpAnswer}</p>}
            </Block>
          )}

          {item.missed.length > 0 && (
            <Block icon={<Warning size={14} weight="bold" />} label="What was thin" tone="amber">
              <ul className="list-disc space-y-1 pl-4">
                {item.missed.map((m) => <li key={m}>{m}</li>)}
              </ul>
            </Block>
          )}

          {item.answer && (
            <Block icon={<CheckCircle size={14} weight="bold" />} label={item.kind === 'behavioural' ? 'How to shape this answer' : 'A strong answer'} tone="teal">
              {item.answer.split(/\n\n+/).map((p, k) => <p key={k} className="mb-2 last:mb-0">{p}</p>)}
              {item.answerSource === 'ai-draft' && (
                <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.14em] text-slate-400">AI-drafted · not a practitioner answer</p>
              )}
            </Block>
          )}
        </Card>
      ))}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-display text-2xl font-semibold tabular-nums text-navy">{value}</p>
      <p className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-slate-400">{label}</p>
    </div>
  );
}

function Block({ icon, label, tone, children }: { icon: React.ReactNode; label: string; tone: 'plain' | 'teal' | 'amber'; children: React.ReactNode }) {
  const toneClass = tone === 'teal' ? 'border-teal-500 bg-teal-50/50' : tone === 'amber' ? 'border-amber-500 bg-amber-50/50' : 'border-slate-200 bg-slate-50/60';
  return (
    <div className={cx('rounded-xl border-l-2 p-4 text-sm leading-relaxed text-slate-700', toneClass)}>
      <p className="mb-1.5 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">
        {icon} {label}
      </p>
      {children}
    </div>
  );
}
